import React from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'
import { TransitOperationFormValues } from 'features/declaration/models/schemas/transitOperationFormSchema'
import { REDUCED_DATASET_AUTHORISATION_TYPE } from 'shared/ncts/common/classifier-util'
import useSoftDeleteFieldArray from 'shared/rhf/useSoftDeleteFieldArray'
import useCodelist from 'features/declaration/codelist/api/useCodelist'
import { blankAuthorisations } from 'features/declaration/models/blankItems'
import FormCollapsibleColumn from 'components/p5/FormCollapsibleColumn'
import FormSelect from 'components/p5/FormSelect'
import FormField from 'components/p5/FormField'
import Input from 'components/p5/Input'
import FormFieldArray from 'components/p5/FormFieldArray'
import { ISelectOption } from 'types/IClassifier'
import useDeclarationFieldRequirements
  from 'features/declaration/form/components/useDeclarationFieldRequirements'

const scope = 'authorisations'

const filterReducedDatasetAuthorisationType = (
  reducedDatasetIndicator: string | null,
  authorisationOption: ISelectOption,
): boolean => reducedDatasetIndicator === '1' || authorisationOption.value !== REDUCED_DATASET_AUTHORISATION_TYPE

function Authorisations() {
  const { t } = useTranslation()

  const {
    getValues,
    control,
  } = useFormContext<TransitOperationFormValues>()

  const {
    visibleFields,
    softRemove,
    appendWithSequence,
  } = useSoftDeleteFieldArray<TransitOperationFormValues>({
    control,
    name: scope,
  })

  const reducedDatasetIndicator = getValues('reducedDatasetIndicator')
  const [, authorisationsOptions] = useCodelist('AUTHORISATIONS')

  const fieldRequirements = useDeclarationFieldRequirements<TransitOperationFormValues>('transitOperation', [
    'authorisations',
  ])
  const isAddForbidden = fieldRequirements.authorisations?.requirement === 'forbidden'
  const isAddButtonDisabled = isAddForbidden || visibleFields.length >= 9

  return (
    <section className="py-3">
      <FormCollapsibleColumn
        columnId="declaration-authorisations"
        headingLabel={t('declaration.p5.authorisations')}
        fieldRule={fieldRequirements.authorisations}
      >
        {visibleFields.map((fieldWithIndex) => {
          const authorisation = fieldWithIndex.field
          const innerItemScope = `${scope}.${fieldWithIndex.index}` as const

          return (
            <FormFieldArray
              key={`authorisations${authorisation.id}`}
              titleKey="declaration.p5.sequenceNumber"
              removeItem={() => softRemove(fieldWithIndex.index)}
              sequenceNumber={authorisation.sequenceNumber + 1}
              hidden={authorisation.deleted}
            >
              <FormField labelKey="declaration.p5.authorisationType">
                <FormSelect
                  field={`${innerItemScope}.type`}
                  labelKey="declaration.p5.authorisationType"
                  options={authorisationsOptions
                    .filter((option) => filterReducedDatasetAuthorisationType(reducedDatasetIndicator, option))}
                  type="sync"
                />
              </FormField>

              <FormField labelKey="declaration.p5.authorisationReferenceNumber">
                <Input
                  field={`${innerItemScope}.referenceNumber`}
                  labelKey="declaration.p5.authorisationReferenceNumber"
                  type="text"
                  placeholder={t('declaration.p5.authorisationReferenceNumber')}
                />
              </FormField>
            </FormFieldArray>
          )
        })}
        <div className="row">
          <div className={`col-12 text-end ${isAddButtonDisabled ? 'cursor--not-allowed' : ''}`}>
            <button
              type="button"
              className="btn btn-sm btn-primary mt-2"
              onClick={() => appendWithSequence(blankAuthorisations)}
              disabled={isAddButtonDisabled}
            >
              <i className="fa fa-plus me-2" />
              {t('declaration.p5.addNewAuthorisation')}
            </button>
          </div>
        </div>
      </FormCollapsibleColumn>
    </section>
  )
}

export default Authorisations
